import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePagination, useSortBy, useTable } from 'react-table';
import { Button, Dropdown } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { ContentHeader } from '../../components/private-layout';
import { ContentBody } from '../../components/private-layout/ContentBody';
import { Table } from '../../components/table';
import { useTeamInfo } from '../../team-id-context';
import { getChecklistTemplatesByTeam } from '../../api/checklist-queries';
import LoadingSpinner from '../../components/loading-spinner';
import ErrorMessage from '../../components/error-message';
import { graphqlErrorsToUiErrors } from '../../api';
import { useSlideout } from '../../components/slideout';
import Tooltip from '../../components/tooltip';
import { DropdownBody } from '../../components/dropdown';
import SearchInput from '../../components/search-input';
import { ReactComponent as DotsIcon } from '../../shared/icons/dotsRoundIcon.svg';
import { ReactComponent as GlobeIcon } from '../../shared/icons/globe-icon.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import ChecklistTemplateSlideout from './slideouts/ChecklistTemplateSlideout';
import CloneChecklistTemplateModal from './modals/CloneChecklistTemplateModal';
import ShareChecklistTemplateModal from './modals/ShareChecklistTemplateModal';
import DeleteChecklistTemplateModal from './modals/DeleteChecklistTemplateModal';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { fillPlaceholders, paths, useNavigate } from '../../routing';
import styles from './styles.module.scss';

interface ChecklistTemplate {
  id: string;
  name: string;
  description?: string | null;
  sharedToAffiliatedWorkspaces: boolean;
  items?: {
    id: string;
    content: string;
    displayOrder: number;
    defaultDueOffsetDays?: number | null;
    children?: any[];
  }[];
}

// Custom toggle component for action dropdown
const CustomToggle = React.forwardRef<any, { onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void; children: React.ReactNode }>(
  ({ children, onClick }, ref) => (
    <a
      className={styles.dottedDropdown}
      href="#/"
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
      <DotsIcon />
    </a>
  )
);

const ChecklistTemplates = () => {
  const { formatMessage } = useIntl();
  const { teamId } = useTeamInfo();
  const navigate = useNavigate();
  const { restrictionsLoading, hasAccessToChecklists } = useUserRestrictions();

  const createSlideout = useSlideout();
  const [selectedTemplate, setSelectedTemplate] = useState<ChecklistTemplate | null>(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSubscriptionModalActive, setSubscriptionModalActive] = useState(false);

  const { data, loading, error, refetch } = useQuery(getChecklistTemplatesByTeam, {
    variables: { teamId },
    skip: !teamId || !hasAccessToChecklists,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Refetch when teamId changes to ensure fresh data
  useEffect(() => {
    if (teamId) {
      refetch();
    }
  }, [teamId, refetch]);

  const getItemCount = React.useCallback((template: ChecklistTemplate): number => {
    const items = template.items || [];
    let count = items.length;
    items.forEach((item) => {
      if (item.children) {
        count += item.children.length;
      }
    });
    return count;
  }, []);

  const handleRowClick = React.useCallback((row: any) => {
    const template = row.original;
    setSelectedTemplate(template);
    createSlideout.open();
  }, []);

  const handleEdit = React.useCallback((template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    createSlideout.open();
  }, []);

  const handleClone = React.useCallback((template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setShowCloneModal(true);
  }, []);

  const handleShare = React.useCallback((template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setShowShareModal(true);
  }, []);

  const handleDelete = React.useCallback((template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  }, []);

  const handleCreateNew = () => {
    if (hasAccessToChecklists) {
      setSelectedTemplate(null);
      createSlideout.open();
    } else {
      setSubscriptionModalActive(true);
    }
  };

  const handleSlideoutClose = () => {
    setSelectedTemplate(null);
    createSlideout.close();
  };

  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="title.name" defaultMessage="Name" />,
        accessor: 'name',
        Cell: ({ cell: { value } }: any) => {
          return <span className={styles.templateName}>{value}</span>;
        },
      },
      {
        Header: <FormattedMessage id="title.description" defaultMessage="Description" />,
        accessor: 'description',
        Cell: ({ cell: { value } }: any) => {
          return <span className="text-muted">{value || '—'}</span>;
        },
      },
      {
        Header: <FormattedMessage id="title.items" defaultMessage="Items" />,
        accessor: 'itemCount',
        Cell: ({ cell: { value } }: any) => {
          return <span>{value} {value === 1 ? 'item' : 'items'}</span>;
        },
        className: 'non-mobile',
        headerClassName: 'non-mobile',
      },
      {
        Header: '',
        accessor: 'actions',
        width: 50,
        Cell: ({ row: { original } }: any) => {
          return (
            <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
              {original.sharedToAffiliatedWorkspaces && (
                <Tooltip
                  placement="bottom"
                  testId="workspace-share-tooltip"
                  icon={<GlobeIcon className="text-primary" style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
                >
                  <FormattedMessage id="checklist-template.shared-tooltip" defaultMessage="Shared to affiliated workspaces" />
                </Tooltip>
              )}
              <Dropdown>
                <Dropdown.Toggle id={`dropdown-actions-${original.id}`} as={CustomToggle}>
                </Dropdown.Toggle>
                <Dropdown.Menu alignRight>
                  <DropdownBody>
                    <Dropdown.Item as={Button} variant="link" onClick={() => handleEdit(original)}>
                      <FormattedMessage id="cta.edit" defaultMessage="Edit" />
                    </Dropdown.Item>
                    <Dropdown.Item as={Button} variant="link" onClick={() => handleClone(original)}>
                      <FormattedMessage id="cta.clone" defaultMessage="Clone" />
                    </Dropdown.Item>
                    <Dropdown.Item as={Button} variant="link" onClick={() => handleShare(original)}>
                      {original.sharedToAffiliatedWorkspaces ? (
                        <FormattedMessage id="cta.unshare" defaultMessage="Unshare" />
                      ) : (
                        <FormattedMessage id="cta.share" defaultMessage="Share" />
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item as={Button} variant="link" onClick={() => handleDelete(original)}>
                      <FormattedMessage id="cta.delete" defaultMessage="Delete" />
                    </Dropdown.Item>
                  </DropdownBody>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          );
        },
        disableSortBy: true,
      },
    ],
    [handleEdit, handleClone, handleShare, handleDelete]
  );

  const tableData = useMemo(() => {
    const templates = data?.checklistTemplatesByTeam || [];
    const filteredTemplates = searchText
      ? templates.filter((template) =>
          template.name.toLowerCase().includes(searchText.toLowerCase()) ||
          template.description?.toLowerCase().includes(searchText.toLowerCase())
        )
      : templates;
    return filteredTemplates.map((template) => ({
      ...template,
      itemCount: getItemCount(template),
      onRowClick: handleRowClick,
    }));
  }, [data, getItemCount, handleRowClick, searchText]);

  //@ts-ignore
  const checklistTable = useTable({ data: tableData, columns }, useSortBy, usePagination);

  // Redirect free users to deals page with upgrade modal
  useEffect(() => {
    if (!restrictionsLoading && !hasAccessToChecklists) {
      navigate(fillPlaceholders(paths.deals.landing, { teamId }) + '?upgrade=checklists');
    }
  }, [restrictionsLoading, hasAccessToChecklists, navigate, teamId]);

  // Early return to prevent rendering for users without access
  if (!restrictionsLoading && !hasAccessToChecklists) {
    return null;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    const errors = graphqlErrorsToUiErrors(error);
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  return (
    <>
      <section className={styles.settingsContainer}>
        <Helmet title={formatMessage({ id: 'title.checklist-templates', defaultMessage: 'Checklist Templates' })} />

        <ContentHeader>
          <div className={classNames(styles.settingsContainer__title, 'ml-3')} role={'heading'}>
            <FormattedMessage id="title.checklist-templates" defaultMessage="Checklist Templates" />
          </div>
        </ContentHeader>

        <ContentBody className={styles.contentBodyFull}>
          <div className="mb-3 non-mobile mx-3">
            <div className="d-flex align-items-center justify-content-between">
              <SearchInput
                className="mr-3"
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                placeholder={formatMessage({
                  id: 'checklist-template.search.placeholder',
                  defaultMessage: 'Search templates...',
                })}
                aria-label={formatMessage({
                  id: 'checklist-template.search.label',
                  defaultMessage: 'Search Templates',
                })}
              />
              <Button variant="secondary" onClick={handleCreateNew}>
                <FormattedMessage id="checklist-template.create" defaultMessage="Create Template" />
              </Button>
            </div>
          </div>
          <Row className="mobile">
            <Col className={'d-flex align-items-center'} style={{ padding: '16px', gap: '16px' }}>
              <SearchInput
                iconClassName="mobileSearch__icon"
                inputClassName="mobileSearch"
                className="flex-grow-1"
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                placeholder={formatMessage({
                  id: 'checklist-template.search.placeholder',
                  defaultMessage: 'Search templates...',
                })}
                aria-label={formatMessage({
                  id: 'checklist-template.search.label',
                  defaultMessage: 'Search Templates',
                })}
              />
              <Button className="addBtn" onClick={handleCreateNew}>
                <PlusIcon width={24} height={24} />
              </Button>
            </Col>
          </Row>

          {/* Desktop Table */}
          <div className={`${styles.scrollableContainer} non-mobile`}>
            <Row>
              <Col>
                {tableData.length === 0 ? (
                  <div className="text-center p-5">
                    <h5 className="text-muted mb-3">
                      {searchText ? (
                        <FormattedMessage
                          id="checklist-template.empty.search"
                          defaultMessage="No templates found matching '{searchText}'"
                          values={{ searchText }}
                        />
                      ) : (
                        <FormattedMessage id="checklist-template.empty.title" defaultMessage="No checklist templates yet" />
                      )}
                    </h5>
                    <p className="text-muted">
                      {searchText ? (
                        <FormattedMessage
                          id="checklist-template.empty.search.description"
                          defaultMessage="Try adjusting your search terms."
                        />
                      ) : (
                        <FormattedMessage
                          id="checklist-template.empty.description"
                          defaultMessage="Create a template to get started."
                        />
                      )}
                    </p>
                  </div>
                ) : (
                  <div className={styles.checklistTableContainer}>
                    <Table striped isStickyHeader paginationBelow isStickyPagination table={checklistTable} />
                  </div>
                )}
              </Col>
            </Row>
          </div>

          {/* Mobile Table */}
          <div className={classNames(styles.mobileTableContainer, 'mobile')}>
            {tableData.length === 0 ? (
              <div className="text-center p-5">
                <h5 className="text-muted mb-3">
                  {searchText ? (
                    <FormattedMessage
                      id="checklist-template.empty.search"
                      defaultMessage="No templates found matching '{searchText}'"
                      values={{ searchText }}
                    />
                  ) : (
                    <FormattedMessage id="checklist-template.empty.title" defaultMessage="No checklist templates yet" />
                  )}
                </h5>
                <p className="text-muted">
                  {searchText ? (
                    <FormattedMessage
                      id="checklist-template.empty.search.description"
                      defaultMessage="Try adjusting your search terms."
                    />
                  ) : (
                    <FormattedMessage
                      id="checklist-template.empty.description"
                      defaultMessage="Create a template to get started."
                    />
                  )}
                </p>
              </div>
            ) : (
              <Table striped isStickyHeader paginationBelow isStickyPagination table={checklistTable} />
            )}
          </div>
        </ContentBody>
      </section>

      {/* Slideout for Create/Edit */}
      <ChecklistTemplateSlideout
        isOpen={createSlideout.isOpen}
        onClose={handleSlideoutClose}
        teamId={teamId}
        template={selectedTemplate}
        refetch={refetch}
      />

      {/* Modals */}
      <CloneChecklistTemplateModal
        show={showCloneModal}
        setShow={setShowCloneModal}
        template={selectedTemplate}
        currentTeamId={teamId}
        refetch={refetch}
      />

      <ShareChecklistTemplateModal
        show={showShareModal}
        setShow={setShowShareModal}
        template={selectedTemplate}
        refetch={refetch}
      />

      <DeleteChecklistTemplateModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        template={selectedTemplate}
        refetch={refetch}
      />

      <SubscriptionRequiredModal
        isOpen={isSubscriptionModalActive}
        onClose={() => setSubscriptionModalActive(false)}
      />
    </>
  );
};

export default ChecklistTemplates;
